<template>
  <div id="app">
    <button
      type="button"
      class="floating-zoom-btn"
      :aria-label="zoomedIn ? 'Zoom out (fit window)' : 'Zoom in'"
      @click="onZoomClick"
    >
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <circle cx="11" cy="11" r="8" />
        <path d="m21 21-4.35-4.35" />
      </svg>
    </button>
    <button
      type="button"
      class="floating-help-btn"
      aria-label="About & MIDI CC chart"
      @click="showHelpModal = true"
    >
      ?
    </button>
    <div
      v-if="showHelpModal"
      class="help-modal-overlay"
      @click.self="showHelpModal = false"
    >
      <div class="help-modal">
        <button
          type="button"
          class="help-modal__close"
          aria-label="Close"
          @click="showHelpModal = false"
        >
          ×
        </button>
        <h1 class="help-modal__title">Vue-Synth</h1>
        <p class="help-modal__tagline">community project adapted for WAX</p>
        <h2 class="help-modal__heading">MIDI CC Chart</h2>
        <div class="help-modal__table-wrap">
          <table class="help-modal__table">
            <thead>
              <tr>
                <th>CC #</th>
                <th>Parameter</th>
                <th>Range</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in ccChartRows" :key="row.cc">
                <td>{{ row.cc }}</td>
                <td>{{ row.param }}</td>
                <td>{{ row.range }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
    <div
      class="zoom-wrapper"
      :class="{ 'zoom-wrapper--fit': !zoomedIn }"
    >
      <div
        ref="synthScaleContainer"
        class="synth-scale-container"
        :style="zoomStyle"
      >
        <div class="flex m-auto" style="z-index:5">
          <Synthesizer ref="synth" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Synthesizer from "@/components/Synthesizer";
import { DEFAULT_CC_MAP } from "@/midi";

const FIT_PADDING_TOP = 48; // room for zoom button

export default {
  name: "App",
  components: { Synthesizer },
  data() {
    return {
      zoomedIn: false,
      fitScale: 1,
      showHelpModal: false
    };
  },
  computed: {
    ccChartRows() {
      // Re-run when modal opens so refs are available
      this.showHelpModal;
      const synth = this.$refs.synth && this.$refs.synth.synth;
      const hasSynth = synth && synth.ready;
      return Object.entries(DEFAULT_CC_MAP)
        .map(([cc, param]) => {
          let range = "—";
          if (hasSynth) {
            const control = synth.getControl(param);
            if (control && control.min !== undefined && control.max !== undefined) {
              const min = Number(control.min);
              const max = Number(control.max);
              const units = control.units ? ` ${control.units}` : "";
              range = `${min}–${max}${units}`;
            }
          }
          return { cc: Number(cc), param, range };
        })
        .sort((a, b) => a.cc - b.cc);
    },
    zoomStyle() {
      if (this.zoomedIn) return {};
      return {
        transform: `scale(${this.fitScale})`,
        transformOrigin: "center center"
      };
    }
  },
  mounted() {
    this._resizeObserver = new ResizeObserver(() => {
      if (!this.zoomedIn) this.updateFitScale();
    });
    window.addEventListener("resize", this._onResize);
    this.$nextTick(() => {
      if (!this.zoomedIn) this.updateFitScale();
      const el = this.$refs.synth && this.$refs.synth.$el;
      if (el) this._resizeObserver.observe(el);
    });
  },
  beforeDestroy() {
    if (this._resizeObserver && this.$refs.synth && this.$refs.synth.$el) {
      this._resizeObserver.unobserve(this.$refs.synth.$el);
    }
    window.removeEventListener("resize", this._onResize);
  },
  methods: {
    onZoomClick() {
      this.zoomedIn = !this.zoomedIn;
      if (!this.zoomedIn) this.$nextTick(() => this.updateFitScale());
    },
    updateFitScale() {
      const synthEl = this.$refs.synth && this.$refs.synth.$el;
      if (!synthEl) return;
      const w = synthEl.offsetWidth;
      const h = synthEl.offsetHeight;
      if (!w || !h) return;
      const maxW = window.innerWidth;
      const maxH = window.innerHeight - FIT_PADDING_TOP;
      const scale = Math.min(maxW / w, maxH / h, 1);
      this.fitScale = scale;
    },
    _onResize() {
      if (!this.zoomedIn) this.updateFitScale();
    }
  }
};
</script>

<style lang="scss">
@import "@/assets/scss/main.scss";

.floating-zoom-btn {
  position: fixed;
  top: 1rem;
  left: 1rem;
  z-index: 10;
  width: 2.5rem;
  height: 2.5rem;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 0.4em;
  background: #181b1c;
  color: #e4e8ea;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.4),
    inset 0 1px 0 rgba(255, 255, 255, 0.06);
  transition: background 0.2s, transform 0.15s;

  svg {
    width: 1.25rem;
    height: 1.25rem;
  }

  &:hover {
    background: #252a2c;
  }

  &:active {
    transform: scale(0.96);
  }
}

.floating-help-btn {
  position: fixed;
  top: calc(1rem + 2.5rem + 0.5rem);
  left: 1rem;
  z-index: 10;
  width: 2.5rem;
  height: 2.5rem;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 0.4em;
  background: #181b1c;
  color: #e4e8ea;
  font-size: 1.25rem;
  font-weight: 600;
  line-height: 1;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.4),
    inset 0 1px 0 rgba(255, 255, 255, 0.06);
  transition: background 0.2s, transform 0.15s;

  &:hover {
    background: #252a2c;
  }

  &:active {
    transform: scale(0.96);
  }
}

.help-modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: rgba(0, 0, 0, 0.65);
  animation: help-fade-in 0.2s ease-out;
}

.help-modal {
  position: relative;
  max-width: 32rem;
  width: 100%;
  max-height: 90vh;
  overflow: auto;
  background: #1c2022;
  border-radius: 0.5rem;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
  padding: 1.5rem 2rem 1.5rem 1.5rem;
  animation: help-slide-in 0.25s ease-out;
}

.help-modal__close {
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  width: 2rem;
  height: 2rem;
  padding: 0;
  border: none;
  border-radius: 0.25rem;
  background: transparent;
  color: #9ca3a8;
  font-size: 1.5rem;
  line-height: 1;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;

  &:hover {
    background: #252a2c;
    color: #e4e8ea;
  }
}

.help-modal__title {
  margin: 0 0 0.25rem;
  font-size: 1.5rem;
  font-weight: 600;
  color: #e4e8ea;
}

.help-modal__tagline {
  margin: 0 0 1.25rem;
  font-size: 0.875rem;
  color: #9ca3a8;
}

.help-modal__heading {
  margin: 0 0 0.75rem;
  font-size: 1rem;
  font-weight: 600;
  color: #c5cbd0;
}

.help-modal__table-wrap {
  overflow-x: auto;
  border-radius: 0.35rem;
  border: 1px solid #2d3336;
}

.help-modal__table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8125rem;

  th,
  td {
    padding: 0.5rem 0.75rem;
    text-align: left;
    border-bottom: 1px solid #2d3336;
  }

  th {
    background: #252a2c;
    color: #9ca3a8;
    font-weight: 600;
  }

  tr:last-child td {
    border-bottom: none;
  }

  td {
    color: #e4e8ea;
  }

  tbody tr:hover td {
    background: rgba(255, 255, 255, 0.03);
  }
}

@keyframes help-fade-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes help-slide-in {
  from {
    opacity: 0;
    transform: translateY(-0.5rem);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.zoom-wrapper {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 0;

  &--fit {
    position: fixed;
    inset: 0;
    z-index: 0;
    overflow: hidden;
    pointer-events: none;
  }

  &--fit .synth-scale-container {
    pointer-events: auto;
  }

  &--fit ::v-deep .synth__wrapper {
    margin-left: 0;
    margin-right: 0;
  }
}

.synth-scale-container {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease-out;
}
</style>
